import React, { useEffect, useState } from "react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { SimpleGrid, Stack, Flex, InputGroup, Text } from "@chakra-ui/react";
import { Plus, Search } from "lucide-react";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";
import NoDataFound from "components/NoDataGraphics/NoDataFound";
import AddHelpTopicDrawer from "./components/AddHelpTopic";
import HelpTopicCard from "./components/HelpTopicCard";
import EditHelpTopicDrawer from "./components/EditHelpTopic";
import ArchiveHelpTopic from "./components/ArchiveTopic";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import { fetchHelpTopicsAction, createHelpTopicAction, updateHelpTopicAction, deleteHelpTopicAction } from "state/actions/helpTopicActions";

const HelpTopicsTab = () => {
  const dispatch = useDispatch();

  // Redux state
  const { helpTopics, loading, error } = useSelector((state) => state.helpTopics);

  const [searchTerm, setSearchTerm] = useState("");
  const [isAddHelpTopicDrawerOpen, setAddHelpTopicDrawerOpen] = useState(false);
  const [isEditHelpTopicDrawerOpen, setEditHelpTopicDrawerOpen] = useState(false);
  const [selectedHelpTopic, setSelectedHelpTopic] = useState(null);
  const [isArchiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);

  useEffect(() => {
    dispatch(fetchHelpTopicsAction());
  }, [dispatch]);

  const handleOpenEditHelpTopicDrawer = (topic) => {
    console.log("Selected Topic:", topic);
    setSelectedHelpTopic(topic);
    setEditHelpTopicDrawerOpen(true);
  };


  const handleCloseEditHelpTopicDrawer = () => setEditHelpTopicDrawerOpen(false);

  const handleOpenAddHelpTopicDrawer = () => setAddHelpTopicDrawerOpen(true);

  const handleCloseAddHelpTopicDrawer = () => setAddHelpTopicDrawerOpen(false);

  const handleAddHelpTopic = (newTopic) => {
    dispatch(createHelpTopicAction(newTopic));
    handleCloseAddHelpTopicDrawer();
  };

  const handleUpdateHelpTopic = (updatedTopic) => {
    dispatch(updateHelpTopicAction(updatedTopic.topic_id, updatedTopic));
    console.log("Updated Topic:", updatedTopic);
    handleCloseEditHelpTopicDrawer();
  };

  const handleArchiveTopic = (topic) => {
    setSelectedTopic(topic);
    setArchiveModalOpen(true);
  };

  const confirmArchiveTopic = () => {
    dispatch(deleteHelpTopicAction(selectedTopic.topic_id));
    setArchiveModalOpen(false);
  };

  // Filter help topics based on search term
  const filteredHelpTopics = helpTopics.filter((topic) =>
    topic.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Stack spacing={5}>
      <Flex justifyContent="space-between" alignItems="center">
        {loading ? (
          // Skeleton for the input
          <Skeleton width="300px" height="40px" borderRadius="12px" ml={5} />
        ) : (
          <InputGroup width="300px" ml={5} borderRadius={"12px"}>
            <ReusableInput
              icon={<Search size={20} color="#A0AEC0" />}
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        )}

        {loading ? (
          // Skeleton for the button
          <Skeleton width="140px" height="40px" borderRadius="12px" mr={5} />
        ) : (
          <GradientButton
            icon={<Plus size={16} />}
            onClick={handleOpenAddHelpTopicDrawer}
            height={"40px"}
            mr={5}
          >
            Add New Topic
          </GradientButton>
        )}
      </Flex>
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
          spacing={5}
          p={5}
          mt={20}
        >
          {filteredHelpTopics.map((topic) => (
            <HelpTopicCard
              key={topic.id}
              {...topic}
              onEdit={() => handleOpenEditHelpTopicDrawer(topic)}
              onArchive={() => handleArchiveTopic(topic)}
              loading={loading}
            />
          ))}
        </SimpleGrid>
      {filteredHelpTopics.length === 0 && (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="300px"
          width="100%"
        >
          <NoDataFound message="No help topics found matching your search." />
        </Flex>
      )}

      <AddHelpTopicDrawer
        isOpen={isAddHelpTopicDrawerOpen}
        onClose={handleCloseAddHelpTopicDrawer}
        onAddHelpTopic={handleAddHelpTopic}
      />

      {selectedHelpTopic && (
        <EditHelpTopicDrawer
          isOpen={isEditHelpTopicDrawerOpen}
          onClose={handleCloseEditHelpTopicDrawer}
          helpTopic={selectedHelpTopic}
          onUpdateHelpTopic={handleUpdateHelpTopic}
        />
      )}

      <ArchiveHelpTopic
        isOpen={isArchiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={confirmArchiveTopic}
        topicTitle={selectedTopic ? selectedTopic.title : ""}
      />
    </Stack>
  );
};

export default HelpTopicsTab;
